import * as React from 'react';
import { useSelector } from 'react-redux';
import { useMessageContextMenu, useScrollOnActiveReplyPreview, useScrollToReplyTarget, useUserColor } from './hooks';
import { trackShowMessage } from './tracking';
import { ChatMessageChatbotGreeting } from '../../components/ChatMessageChatbotGreeting';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { MessageProvider } from '../MessageProvider/MessageProvider';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { ChatMessageOfferGalleryContainer } from '../ChatMessageOfferGalleryContainer';
import { ChatMessageLayout } from '../../components/ChatMessageLayout';
import { useMessageType } from './hooks/useMessageType';
import { ChatMessageReplyLayout } from '../../components/ChatMessageReplyLayout/ChatMessageReplyLayout';
import { DocumentMessageContainer } from './containers/DocumentMessageContainer';
import { OfferMessageContainer } from './containers/OfferMessageContainer';
import { ChatbotOfferMessageContainer } from './containers/ChatbotOfferMessageContainer';
import { DeletedMessageContainer } from './containers/DeletedMessageContainer';
import { TextAndImagesMessageContainer } from './containers/TextAndImagesMessageContainer';
import { BubblesContainer } from './containers/BubblesContainer';

type TChatMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
  isLastInChain: boolean;
  isFirstUserPhrase: boolean;
};

export const ChatMessageContainer: React.FC<TChatMessageContainerProps> = ({
  message,
  messageId,
  isLastInChain,
  isFirstUserPhrase,
}) => {
  const { status: messageStatus, communicationId, isDeleted, offers, isOutcoming, type: messageKind } = message;

  const chatId = useSelector(currentOpenChatIDSelector) || '';

  const messageRef = React.useRef<HTMLDivElement | null>(null);

  const { cancelLongPress } = useMessageContextMenu({ messageId, isDeleted, messageRef });

  const messageType = useMessageType(message);

  const { highlighted, onHighlightEnd } = useScrollToReplyTarget({ messageId, messageRef });

  const userColor = useUserColor(message);

  useScrollOnActiveReplyPreview({ messageId, messageRef });

  const onShow = React.useCallback(() => {
    const isChatbotOfferMessage = chatId === CHATBOT_ID && Boolean(offers?.length) && !isOutcoming;
    const attributes = isChatbotOfferMessage ? 'Horizontal_scroll ' : undefined;

    trackShowMessage({
      chatId,
      messageId,
      communicationId,
      attributes,
      offer: offers,
    });
  }, [chatId, messageId, communicationId, offers, isOutcoming]);

  const isChatbotGreetingMessage = messageKind === 'textAndImages';
  const isOfferGalleryMessage = offers && offers.length > 1;
  const isChatbotChat = chatId === CHATBOT_ID;

  return (
    <MessageProvider
      message={message}
      messageId={messageId}
      chatId={chatId}
      messageType={messageType}
      isLastInChain={isLastInChain}
      isFirstUserPhrase={isFirstUserPhrase}
      messageRef={messageRef}
      cancelLongPress={cancelLongPress}
      highlighted={highlighted}
      userColor={userColor}
      onHighlightEnd={onHighlightEnd}
    >
      <ChatMessageLayout messageStatus={messageStatus} messageType={messageType} onShow={onShow}>
        {isChatbotGreetingMessage && <ChatMessageChatbotGreeting />}

        {!isChatbotGreetingMessage && isOfferGalleryMessage && <ChatMessageOfferGalleryContainer />}

        {!isChatbotGreetingMessage && !isOfferGalleryMessage && (
          <ChatMessageReplyLayout bubbles={<BubblesContainer />}>
            <DeletedMessageContainer />
            {isChatbotChat ? <ChatbotOfferMessageContainer /> : <OfferMessageContainer />}
            <DocumentMessageContainer />
            <TextAndImagesMessageContainer />
          </ChatMessageReplyLayout>
        )}
      </ChatMessageLayout>
    </MessageProvider>
  );
};
